import React, { useState, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { useUnsavedContext, BlockerDialog } from './UnsavedChangesDialog';
import {
  LayoutDashboard,
  Home,
  Settings,
  FileText,
  DollarSign,
  Building2,
  ShoppingCart,
  Tags,
  Receipt,
  FileSpreadsheet,
  TrendingUp,
  Calculator,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  LogOut,
  BookOpen,
  Shield,
  Brain,
  Lock
} from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const mainMenuItems = [
  { path: '/app', label: 'Inicio', icon: Home },
  { path: '/app/admin', label: 'Admin', icon: Shield, adminOnly: true }
];

const projectMenuItems = [
  { path: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: 'specifications', label: '1. Especificaciones', icon: FileText },
  { path: 'investment', label: '2. Inversion Necesaria', icon: Building2 },
  { path: 'financing', label: '3. Financiamiento', icon: DollarSign },
  { path: 'sales-volume', label: '4. Volumen de Ventas', icon: ShoppingCart },
  { path: 'pricing', label: '5. Precio y Costos', icon: Tags },
  { path: 'fixed-expenses', label: '7. Gastos Fijos', icon: Receipt },
  { path: 'income-statement', label: '8. Estado de Resultados', icon: FileSpreadsheet, premium: true },
  { path: 'cash-flow', label: '9. Flujo de Efectivo', icon: TrendingUp, premium: true },
  { path: 'valuation', label: '10. Valuacion', icon: Calculator, premium: true },
  { path: 'scenarios', label: '11. Escenarios y PDF', icon: FileText, premium: true },
  { path: 'explained', label: '12. Tu Proyecto Explicado', icon: BookOpen, premium: true }
];

export const Sidebar = ({ collapsed, onToggle }) => {
  const { currentProject } = useProject();
  const { user, logout, hasActivePlan } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [projectMenuOpen, setProjectMenuOpen] = useState(true);
  const unsavedCtx = useUnsavedContext();
  const [blockerDialog, setBlockerDialog] = useState({ show: false, path: null, saveFn: null });
  const [saving, setSaving] = useState(false);
  const isPremium = hasActivePlan ? hasActivePlan() : false;
  const isDemo = currentProject?.is_demo;
  
  const isProjectRoute = location.pathname.includes('/project/');

  const handleNavClick = useCallback((e, path) => {
    if (!unsavedCtx) return;
    const result = unsavedCtx.check();
    if (result) {
      e.preventDefault();
      setBlockerDialog({ show: true, path, saveFn: result.save });
    }
  }, [unsavedCtx]);

  const handleSaveAndGo = async () => {
    setSaving(true);
    try {
      if (blockerDialog.saveFn) await blockerDialog.saveFn();
    } finally {
      setSaving(false);
    }
    const path = blockerDialog.path;
    setBlockerDialog({ show: false, path: null, saveFn: null });
    navigate(path);
  };

  const handleDiscardAndGo = () => {
    const path = blockerDialog.path;
    setBlockerDialog({ show: false, path: null, saveFn: null });
    navigate(path);
  };

  const handleCancelNav = () => {
    setBlockerDialog({ show: false, path: null, saveFn: null });
  };

  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 h-screen bg-[#0F111A] border-r border-border/30 flex flex-col z-50 transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}
      data-testid="sidebar"
    >
      {/* Logo + Toggle */}
      <div className="p-4 border-b border-border/30 flex items-center justify-between">
        {!collapsed && (
          <img src="/logo-baluad.png" alt="Baluad" className="h-7 object-contain" />
        )}
        <button
          onClick={onToggle}
          className={cn("p-1.5 rounded-lg hover:bg-white/10 transition-colors text-muted-foreground hover:text-foreground", collapsed && "mx-auto")}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      <ScrollArea className="flex-1 py-4">
        {/* Main Menu */}
        <nav className="px-3 space-y-1">
          {mainMenuItems.filter(item => !item.adminOnly || user?.email === 'esteban.davislz@gmail.com' || user?.email === 'consultoria.estebanker@gmail.com' || process.env.REACT_APP_DEV_MODE === 'true').map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`sidebar-${item.path.replace('/', '') || 'home'}`}
              onClick={(e) => handleNavClick(e, item.path)}
              className={({ isActive }) => cn(
                'sidebar-item',
                isActive && !isProjectRoute && 'sidebar-item-active',
                collapsed && 'justify-center px-2'
              )}
              title={collapsed ? item.label : undefined}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Project Menu - Only show when a project is selected */}
        {currentProject && (
          <div className="mt-6 px-3">
            <button
              onClick={() => setProjectMenuOpen(!projectMenuOpen)}
              className={cn("w-full flex items-center justify-between px-4 py-3 rounded-lg bg-primary/10 border border-primary/20 mb-2", collapsed && "px-2 justify-center")}
              data-testid="project-menu-toggle"
              title={collapsed ? currentProject.name : undefined}
            >
              <div className="flex items-center gap-3">
                {currentProject.specifications?.logo ? (
                  <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarImage src={currentProject.specifications.logo} />
                    <AvatarFallback>{currentProject.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-primary" />
                  </div>
                )}
                {!collapsed && (
                  <div className="text-left">
                    <p className="text-sm font-semibold text-foreground truncate max-w-[120px]">
                      {currentProject.name || 'Proyecto'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {currentProject.status === 'draft' ? 'Borrador' : 
                       currentProject.status === 'in_review' ? 'En Revisión' : 'Finalizado'}
                    </p>
                  </div>
                )}
              </div>
              {!collapsed && (projectMenuOpen ? 
                <ChevronDown className="w-4 h-4 text-muted-foreground" /> : 
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
            </button>

            {projectMenuOpen && (
              <nav className="space-y-1 animate-fade-in">
                {projectMenuItems.map((item) => {
                  const isLocked = item.premium && !isPremium && !isDemo;
                  return (
                    <NavLink
                      key={item.path}
                      to={`/app/project/${currentProject.id}/${item.path}`}
                      data-testid={`sidebar-project-${item.path}`}
                      onClick={(e) => handleNavClick(e, `/app/project/${currentProject.id}/${item.path}`)}
                      className={({ isActive }) => cn(
                        'sidebar-item text-sm',
                        isActive && 'sidebar-item-active',
                        collapsed && 'justify-center px-2',
                        item.isAI && 'mt-2 border border-violet-500/20 bg-violet-500/5 hover:bg-violet-500/10 text-violet-300',
                        isLocked && !item.isAI && 'opacity-60'
                      )}
                      title={collapsed ? item.label : undefined}
                    >
                      <item.icon className={cn("w-4 h-4 flex-shrink-0", item.isAI && "text-violet-400")} />
                      {!collapsed && <span>{item.label}</span>}
                      {!collapsed && isLocked && <Lock className="w-3 h-3 ml-auto text-amber-400/70 flex-shrink-0" />}
                    </NavLink>
                  );
                })}
              </nav>
            )}
          </div>
        )}
      </ScrollArea>

      {/* User Section */}
      <div className="p-4 border-t border-border/30 space-y-2">
        <NavLink
          to="/app/settings"
          className={({ isActive }) => cn(
            "flex items-center gap-3 p-2 rounded-lg transition-colors",
            isActive ? "bg-primary/20 text-primary" : "hover:bg-secondary/50",
            collapsed && "justify-center"
          )}
          data-testid="user-settings-btn"
          title={collapsed ? 'Configuración' : undefined}
        >
          <Avatar className="w-9 h-9 flex-shrink-0">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-primary/20 text-primary text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name || 'Usuario'}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email || 'Configuración'}</p>
              </div>
              <Settings className="w-4 h-4 text-muted-foreground" />
            </>
          )}
        </NavLink>
        <button
          onClick={async () => { await logout(); navigate('/'); }}
          className={cn("flex items-center gap-2 w-full p-2 rounded-lg text-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors", collapsed && "justify-center")}
          data-testid="logout-btn"
          title={collapsed ? 'Cerrar sesión' : undefined}
        >
          <LogOut className="w-4 h-4" />
          {!collapsed && 'Cerrar sesión'}
        </button>
      </div>

      <BlockerDialog 
        showDialog={blockerDialog.show} 
        onSave={handleSaveAndGo} 
        onDiscard={handleDiscardAndGo} 
        onCancel={handleCancelNav}
        saving={saving} 
      />
    </aside>
  );
};
